/**
 * Layout-shift watch (#3624, D4). react-joyride re-measures its spotlight and
 * tooltip on window resize and scroll only, so content that loads above the
 * target AFTER the step is shown (a banner, a late table) left the tooltip
 * pointing at empty space. The engine polls the target's position within its
 * scroll container -- scroll-independent, so scrolling never counts as a
 * shift -- and nudges react-joyride with a resize event when it moves. A hint
 * also waits for the target to hold still before it is shown at all.
 */
export const LAYOUT_POLL_MS = 250;
const SETTLE_POLL_MS = 100;
const SETTLE_STABLE_READS = 3;
const SETTLE_MAX_MS = 1500;

function scrollParent(el: HTMLElement): HTMLElement {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/** The element's box within its scroll container, independent of scrolling. */
export function layoutKey(el: HTMLElement): string {
  const rect = el.getBoundingClientRect();
  const parent = scrollParent(el);
  const isRoot = parent === document.scrollingElement || parent === document.documentElement;
  const base = isRoot ? { top: 0, left: 0 } : parent.getBoundingClientRect();
  const top = rect.top - base.top + parent.scrollTop;
  const left = rect.left - base.left + parent.scrollLeft;
  return [top, left, rect.width, rect.height].map((n) => Math.round(n)).join(":");
}

/** Resolve once the target has held still for a few reads (or the cap passes). */
export function waitForLayoutToSettle(
  find: () => HTMLElement | null,
  stillCurrent: () => boolean,
): Promise<void> {
  const deadline = Date.now() + SETTLE_MAX_MS;
  return new Promise((resolve) => {
    let last = "";
    let stable = 0;
    const read = () => {
      const el = find();
      if (!stillCurrent() || !el || Date.now() >= deadline) return resolve();
      const key = layoutKey(el);
      stable = key === last ? stable + 1 : 0;
      last = key;
      if (stable >= SETTLE_STABLE_READS - 1) return resolve();
      window.setTimeout(read, SETTLE_POLL_MS);
    };
    read();
  });
}
